import clsx from 'clsx';
import { MouseEventHandler, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CancelSVG, LOGOSVG } from '../../assets';
import AuthAxios from '../../axios';
import IconButton from '../../components/IconButton';
import { ModalContext } from '../../contexts/ModalContext';
import MarketingInformationPage from '../Consent/MarketingInforamtionPage';
import PrivatePolicyPage from '../Consent/PrivatePolicyPage';
import TermsOfUsePage from '../Consent/TermsOfUsePage';
import { logFirebaseEvent } from '../../utils/firebase/analytics';
import { FirebaseAnalyticsEvent } from '../../types';
import { getItem, USERINFO } from '../../utils/localStorage';

export default function AgrementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allCheck, setAllCheck] = useState(false);
  const [ageCheck, setAgeCheck] = useState(false);
  const [termCheck, setTermCheck] = useState(false);
  const [privateCheck, setPrivateCheck] = useState(false);
  const [smsCheck, setSmsCheck] = useState(false);
  const [thirdPartyCheck, setThirdPartyCheck] = useState(false);
  const user = getItem(USERINFO);

  const onClickCheckAll = (flag: boolean) => {
    if (flag) {
      setAllCheck(flag);
      setTermCheck(flag);
      setPrivateCheck(flag);
      setSmsCheck(flag);
      setAgeCheck(flag);
      setThirdPartyCheck(flag);
    } else {
      setAllCheck(flag);
      setTermCheck(flag);
      setPrivateCheck(flag);
      setSmsCheck(flag);
      setAgeCheck(flag);
      setThirdPartyCheck(flag);
    }
  };
  useEffect(() => {
    // termCheck privateCheck smsCheck ageCheck 하나라도 false이면 setAllCheck(false) 수행한다.
    if (termCheck && privateCheck && smsCheck && ageCheck) {
      setAllCheck(true);
    } else {
      setAllCheck(false);
    }
  }, [termCheck, privateCheck, smsCheck, ageCheck]);

  const onClickStartButton = () => {
    AuthAxios.put('/users/terms-agree', {
      requiredConsents: termCheck && privateCheck && ageCheck,
      pushNotificationConsent: smsCheck,
      thirdPartyInformationConsent: thirdPartyCheck,
    })
      .then((resolve) => {
        logFirebaseEvent(FirebaseAnalyticsEvent.SIGNUP, {
          user_uuid: user.id,
          signup_platform: user.type,
        });
        return navigate('/app/home', { replace: true });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const consents: {
    content: string;
    modal?: JSX.Element;
    isChecked: boolean;
    handleCheck: (checked: boolean) => void;
    className?: string;
  }[] = [
    {
      content: t('web.ageConfirmation'),
      isChecked: ageCheck,
      handleCheck: setAgeCheck,
    },
    {
      content: t('web.termsOfUse'),
      modal: (
        <AgreementModal
          title={t('web.termsOfUse')}
          content={<TermsOfUsePage />}
          check={termCheck}
          checkFunction={setTermCheck}
        />
      ),
      isChecked: termCheck,
      handleCheck: setTermCheck,
    },
    {
      content: t('web.privacyConsent'),
      modal: (
        <AgreementModal
          title={t('web.privacyConsent')}
          content={<PrivatePolicyPage />}
          check={privateCheck}
          checkFunction={setPrivateCheck}
        />
      ),
      isChecked: privateCheck,
      handleCheck: setPrivateCheck,
    },
    {
      content: t('web.marketingConsent'),
      modal: (
        <AgreementModal
          title={t('web.marketingConsent')}
          content={<MarketingInformationPage />}
          check={smsCheck}
          checkFunction={setSmsCheck}
        />
      ),
      isChecked: smsCheck,
      handleCheck: setSmsCheck,
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center w-full h-full">
      <LOGOSVG className="mb-[30px] w-[152px] md:w-[163px]" />
      <div className="text-lg font-bold text-N0 mb-[53px] md:text-xl">{t('web.welcome')}</div>
      <fieldset>
        <div className="w-[288px] h-[222px] mb-[43px] text-N0 flex flex-col gap-[8px] text-[15px] md:w-[360px]">
          <CheckInput
            content={t('web.agreeAll')}
            isChecked={allCheck}
            handleCheck={onClickCheckAll}
            className="text-lg"
          />
          <hr className="w-full my-[24px] bg-N3 " />

          <div className="flex flex-col gap-3">
            {consents.map((c) => (
              <CheckInput
                key={c.content}
                content={c.content}
                modal={c.modal}
                isChecked={c.isChecked}
                handleCheck={c.handleCheck}
              />
            ))}
          </div>
        </div>
      </fieldset>
      <button
        onClick={onClickStartButton}
        disabled={!termCheck || !privateCheck || !ageCheck}
        className={clsx('w-[288px] h-[48px] rounded-lg text-base font-medium md:w-[360px] md:h-[56px]', {
          'bg-N2 text-N4': !(termCheck && privateCheck && ageCheck),
          'bg-N9 text-N2': termCheck && privateCheck && ageCheck,
        })}
      >
        {t('web.start')}
      </button>
    </div>
  );
}

function CheckInput({
  modal,
  isChecked,
  handleCheck,
  content,
  className,
}: {
  modal?: JSX.Element;
  isChecked: boolean;
  handleCheck: (checked: boolean) => void;
  content: string;
  className?: string;
}) {
  const { handleModal } = useContext(ModalContext);

  const labelClickHandler: MouseEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();

    handleCheck(!isChecked);
  };

  const moreClickHandler: MouseEventHandler<HTMLSpanElement> = (event) => {
    event.preventDefault();

    if (modal) {
      handleModal(modal);
    }
  };

  return (
    <div className={clsx('w-full flex flex-row', className)}>
      <label
        className="grow flex flex-row justify-start items-center gap-1 hover:cursor-pointer"
        onClick={labelClickHandler}
      >
        <div className="h-5 aspect-square bg-white rounded-full p-1">
          <div
            className={clsx('w-full h-full rounded-full', {
              'bg-N5': isChecked,
              'bg-white': !isChecked,
            })}
          />
        </div>

        <input
          type="checkbox"
          name="thirdPary"
          checked={isChecked}
          className="appearance-none" // 실제 체크박스 숨김 처리
          onChange={(e) => handleCheck(e.currentTarget.checked)}
        />

        {content}
      </label>
      {modal && (
        <span className="float-right max-md:hidden cursor-pointer" onClick={moreClickHandler}>
          &gt;
        </span>
      )}
    </div>
  );
}

interface IAgreementProps {
  content: React.ReactNode;
  title: string;
  check: boolean;
  checkFunction: Function;
}

export function AgreementModal(props: IAgreementProps) {
  const { t } = useTranslation();
  const { handleModal } = useContext(ModalContext);
  const { content, title, check, checkFunction } = props;
  const [_check, setCheck] = useState(check);
  const handleCheck = (e: boolean) => {
    setCheck(e);
    checkFunction(e);
  };
  return (
    <div className="w-[339px] min-h-[504px] md:w-[788px] md:min-h-[504px] rounded-[20px] bg-N0 px-[18px] py-[15px] flex flex-col items-center">
      <div className="flex justify-end w-full">
        <IconButton
          onClick={() => handleModal()}
          className="w-[24px] h-[24px] rounded-full bg-N9 flex justify-center items-center"
        >
          <CancelSVG />
        </IconButton>
      </div>
      <header className="w-full text-[20px] mb-[34px]">{title}</header>
      <main className="w-[300px] md:w-[742px] md:h-[275px] rounded-[20px] border border-N8 h-[275px] px-[20px] py-[22px]">
        <div className="w-full h-full overflow-y-scroll text-black">{content}</div>
      </main>
      <div className="flex items-center justify-start w-full mt-[14px] mb-[18px] mx-auto">
        <label className="pl-4 py-2 hover:cursor-pointer">
          <input
            type="checkbox"
            checked={_check}
            className="mr-4"
            onChange={(e) => handleCheck(e.currentTarget.checked)}
          />
          {t('web.termsAgreement')}
        </label>
      </div>
      <button onClick={() => handleModal()} className="w-[287px] h-[48px] rounded-lg bg-N9 text-N0 text-[15px]">
        {t('web.confirm')}
      </button>
    </div>
  );
}
